import {
  IPayerEngagementResponse,
  IRequirementResponse,
  PaymentsEligibility,
  PayerPayeeEngagementStatus,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { EngagementType } from "@wingspanhq/payments/dist/interfaces/engagement";
import {
  PayerEngagementStatus,
  PayerRow,
  PayerRowEngagement,
  PayerRowRequirement,
  SearchRowEngagementStatusRollup
} from "@wingspanhq/wingspan-api-sdk";

/**
 * Adapt the v3 denormalized `PayerRow` (search projection) into the v1
 * `IPayerEngagementResponse[]` shape that the payee-side client screens and their
 * selectors (`selectorPayerPayeeEngagements`, `getEngagementsRequirementsCompleted`,
 * `EngagementsTable`, …) still consume, so those render unchanged while the data
 * source moves to v3. The CRUD `PayerEngagement` resource is too lean for this
 * (no requirements / eligibility / isDefault); the search row carries all of it.
 *
 * Field notes:
 * - `status` — v3 `PayerEngagementStatus` (Created/Activated/Suspended/Terminated)
 *   collapses to v1's binary Active/Inactive (only `Activated` is Active).
 * - `paymentsEligibility` — sourced from the row-level `engagementStatus` rollup
 *   (the BE eligibility signal). It is per-row, so every engagement on a payer with
 *   >1 engagement shares the rollup value — an accepted approximation.
 * - `requirements[]` — the row's requirements filtered to each engagement via
 *   `payerEngagementIds`. Only `requirementId` / `status` / `payerId` are on the
 *   lean projection; rich fields (name, dates, dataSource*) degrade to unset. The
 *   rich source is the Requirements list endpoint.
 * - `labels.duplicate` — v1 `labels` → v3 `metadata` (string `"true"`).
 * - `payeeOwnedData.integration.quickbooks` — row-level (payer default) QBO mapping;
 *   the per-engagement mapping lives on the CRUD `PayerEngagement` and is merged in
 *   on the engagement-detail screen.
 */

// v1 RequirementStatus and v3 SearchRowRequirementStatus share identical string
// values (Complete/Incomplete/Inactive/PendingReview) — cast once at the boundary.
const toRequirementStatus = (
  status: PayerRowRequirement["status"]
): RequirementStatus =>
  (status as unknown as RequirementStatus) ?? RequirementStatus.Incomplete;

const toRequirement = (
  requirement: PayerRowRequirement,
  payerId: string | undefined
): IRequirementResponse =>
  ({
    requirementId: requirement.requirementId,
    requirementDefinitionId: requirement.definitionId ?? undefined,
    status: toRequirementStatus(requirement.status),
    payerOverrideStatus: requirement.payerOverrideStatus
      ? toRequirementStatus(requirement.payerOverrideStatus)
      : undefined,
    payerId,
    payerPayeeEngagementIds: requirement.payerEngagementIds ?? []
  }) as unknown as IRequirementResponse;

const selectorPayerEngagementFromRowEngagement = (
  engagement: PayerRowEngagement,
  row: PayerRow
): IPayerEngagementResponse => {
  const status =
    engagement.status === PayerEngagementStatus.ACTIVATED
      ? PayerPayeeEngagementStatus.Active
      : PayerPayeeEngagementStatus.Inactive;

  const requirements = (row.requirements ?? [])
    .filter(requirement =>
      requirement.payerEngagementIds?.includes(engagement.payerEngagementId!)
    )
    .map(requirement => toRequirement(requirement, row.id));

  const paymentsEligibility =
    row.engagementStatus === SearchRowEngagementStatusRollup.ELIGIBLE
      ? PaymentsEligibility.Eligible
      : PaymentsEligibility.NotEligible;

  return {
    payerPayeeEngagementId: engagement.payerEngagementId,
    createdAt: row.events?.createdAt,
    updatedAt: row.events?.updatedAt,
    status,
    events: {},
    isDefault: !!engagement.isDefault,
    engagementId: engagement.engagementId ?? undefined,
    engagementName: engagement.engagementName ?? undefined,
    type: (engagement.engagementType as EngagementType) ?? undefined,
    paymentsEligibility,
    requirements,
    labels: { duplicate: engagement.metadata?.duplicate === "true" },
    payeeOwnedData: {
      integration: {
        quickbooks: {
          customerId: row.quickbooksCustomerId ?? undefined,
          itemId: row.quickbooksItemId ?? undefined
        }
      }
    },
    payerOwnedData: {}
  } as unknown as IPayerEngagementResponse;
};

export const selectorPayerEngagementsFromV3 = (
  row: PayerRow | undefined
): IPayerEngagementResponse[] =>
  (row?.engagements ?? []).map(engagement =>
    selectorPayerEngagementFromRowEngagement(engagement, row!)
  );
